import { AxiosError } from "axios";
import useSWR from "swr";
import { GetFundingHistory200Response } from "../../../codegen-api";
import { APIEndpointEnum } from "../../../enums/endpoint";
import { publicApi } from "../../../services/api/apiFetcher";
import { TimePeriodEnum } from "../../../interfaces/TimePeriod";
import { getStartTimeSeconds } from "../../../utils/date";

type FundingRow = Array<string>;

/**
 * Merge per-instrument funding histories newest first. Rows are
 * [instrument, timestamp (unix ns), rate, mark]; the timestamp is compared as a
 * BigInt because nanoseconds overflow a double's exact range.
 */
/** A merged "All perps" result, plus which instruments did not load. */
export type FundingHistoryResult = GetFundingHistory200Response & {
  failedInstruments?: string[];
};

export const mergeFundingHistories = (
  pages: (GetFundingHistory200Response | undefined)[]
): GetFundingHistory200Response => {
  const rows: FundingRow[] = pages.flatMap((p) => p?.funding_history ?? []);
  const ts = (row: FundingRow) => {
    try {
      return BigInt(row[1] ?? "0");
    } catch {
      return BigInt(0);
    }
  };
  rows.sort((a, b) => {
    const d = ts(b) - ts(a);
    return d > BigInt(0) ? 1 : d < BigInt(0) ? -1 : 0;
  });
  return { funding_history: rows };
};

/**
 * Funding history for one perp, or — with no `instrumentName` — for every perp
 * in `allInstruments`, merged.
 *
 * The backend's /funding-history requires an instrument and answers 400
 * without one, so the "All perps" view (no instrument) never loaded: it sent
 * the bare request and showed an empty table. It now asks per instrument and
 * merges; with neither an instrument nor a list it does not query at all.
 */
export const useFundingHistory = (
  instrumentName?: string,
  limit?: number,
  disableQuery?: boolean,
  period: TimePeriodEnum = TimePeriodEnum.WEEKLY,
  allInstruments?: string[]
) => {
  const fetcher = publicApi();
  const instruments = instrumentName
    ? [instrumentName]
    : (allInstruments ?? []).filter(Boolean);

  return useSWR<FundingHistoryResult | undefined, AxiosError>(
    disableQuery || instruments.length === 0
      ? undefined
      : [APIEndpointEnum.FUNDING_HISTORY, instruments.join(","), period, limit],
    async () => {
      const start = getStartTimeSeconds(period);
      const fetchOne = async (name: string) =>
        (
          await (
            await fetcher.getFundingHistory(name, start, undefined, limit)
          )()
        ).data;
      if (instrumentName) return fetchOne(instrumentName);
      // allSettled, not all: one instrument the funding service rejects (or a
      // 429) used to fail the whole list, which then read as "no funding
      // history" -- and SWR's retry re-ran every request. Merge what loaded and
      // name what didn't; fail only when nothing loaded.
      const settled = await Promise.allSettled(instruments.map(fetchOne));
      const loaded = settled.flatMap((r) =>
        r.status === "fulfilled" ? [r.value] : []
      );
      if (loaded.length === 0) {
        throw (settled[0] as PromiseRejectedResult).reason;
      }
      const failedInstruments = instruments.filter(
        (_, i) => settled[i].status === "rejected"
      );
      return {
        ...mergeFundingHistories(loaded),
        ...(failedInstruments.length ? { failedInstruments } : {}),
      };
    }
  );
};
